/****************************************************
 * Module Name:  stoneEdit.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useState, useEffect } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import Utility from "../../utility/utility";
import renderCellExpand from "../common/dataGridCellExpand";
import { Box, CircularProgress, Tooltip, Button } from "@mui/material";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import CommonSnackbar from "./commonSnackBar";
import Constants from "../../utility/constants";
import { styled } from "@mui/material/styles";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PacketHistoryModal from "./packetHistoryModal";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    "& .super-app-theme--AVAILABLE": {
        color: "#000",
        "&:hover": {
            color: "#000",
        },
    },
    "& .super-app-theme--MEMO_REQUESTED": {
        color: "#F23869",
        "&:hover": {
            color: "#F23869",
        },
    },
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
    }
}));

const StoneEdit = ({
    stoneId,
    setUpdatedData
}) => {

    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();

    useEffect(() => {
        handleSearch(stoneId);
    }, ([]));

    const [rows, setRows] = useState([]);
    const [detailLoader, setDetailLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [columnNames, setColumnNames] = React.useState([]);
    const [kapanId, setKapanId] = useState("");
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);


    const handleSearch = (stoneId) => {
        setDetailLoader(true);
        StoneProxy.GetSingleStone(
            stoneId,
            (responseData) => {
                if (responseData) {
                    setColumnNames(Utility.PrepareColumns(columnsData));
                    responseData.agenumber = Utility.ConvertDateToAge(responseData.age);
                    setRows([responseData]);
                    setDetailLoader(false)
                } else {
                    setErrorSnackbar(true)
                    setDetailLoader(false)
                }
            }
        );
    };

    const columnsData = {
        "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
            field: "stoneid",
            headerName: "STONEID",
            pinning: { position: 'left' },
            headerAlign: "center",
            align: "center",
            width: 80,
            cellClassName: 'stickyColumn',
        },
        "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        "8a492e00-6167-45de-b078-e572e47c8a39": {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid)
                        setPacketId(params.row.packetid)
                        setShowPacketDetailModal(true)
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
        },
        "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 100,
            valueGetter: (params) => (params.value = "AVAILABLE"),
            renderCell: (params) => (params.value = "AVAILABLE"),
        },
        "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            editable: true,
            renderCell: renderCellExpand,
        },
        "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            width: 40,
            align: "right",
            editable: true,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            width: 65,
            editable: true,
            renderCell: renderCellExpand,
        },
        "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            editable: true,
            renderCell: renderCellExpand,
        },
        "3597803f-8932-40c6-8de6-81006f4d6d5b": {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            editable: true,
            renderCell: renderCellExpand,
        },
        "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            editable: true,
            renderCell: renderCellExpand,
        },
        "5541c182-21b8-496e-b342-b0b89f3d302f": {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            editable: true,
            renderCell: renderCellExpand,
        },
        "8296e627-0be5-4a66-830f-ea6867cea137": {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            editable: true,
            renderCell: renderCellExpand,
        },
        "798a8663-0f0c-41be-8512-16dd60d5f25e": {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "right",
            width: 50,
            editable: true,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
        },
        "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "right",
            width: 50,
            editable: true,
            renderCell: (params) => params.row.rapnetprice
        },
        "39c634f1-2644-4307-acbb-983b4de4229e": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            editable: true,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            width: 70,
            editable: true,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        "13d48508-b8c9-4661-8a6c-9fb22a3408a3": {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            editable: true,
            width: 140,
            renderCell: renderCellExpand,
        },
        "567202e6-c31c-4c19-9998-1cacae05af6e": {
            field: "depth",
            headerName: "DEPTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            editable: true,
            renderCell: renderCellExpand,
        },
        "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 40,
            editable: true,
            renderCell: renderCellExpand,
        },
        "19ce7030-402c-495c-84ac-a29093bdb0ac": {
            field: "locationid",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "ab3e506e-bddb-456d-b967-922dcee74d56": {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            editable: true,
            width: 100,
            renderCell: renderCellExpand,
        },
        "f97a0118-3891-4f77-abf7-8636c26d594c": {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "cb7e6add-67ba-4194-ac49-fd0d967db4e8": {
            field: "quality",
            headerName: "QUALITY",
            headerAlign: "center",
            align: "center",
            editable: true,
            width: 80,
            renderCell: renderCellExpand,
        },
        "b718f242-4a82-42e7-b98a-40bbf0f67c21": {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            editable: true,
            width: 60,
            renderCell: renderCellExpand,
        },
        "01e90aca-bdcb-4e8e-80ec-2d7007df8cd3": {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            editable: true,
            width: 120,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        "3c62ef48-9538-4df7-9484-a9ae1ba31086": {
            field: "height",
            headerName: "HEIGHT",
            headerAlign: "center",
            align: "center",
            editable: true,
            width: 70,
            renderCell: renderCellExpand,
        },
        "eed9b7eb-17e4-4ec6-9c9d-c299f885544c": {
            field: "width",
            headerName: "WIDTH",
            headerAlign: "center",
            align: "center",
            editable: true,
            width: 70,
            renderCell: renderCellExpand,
        },
        "8eaa8a03-d163-436d-8912-51ea06306f44": {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 100,
            editable: true,
            renderCell: renderCellExpand,
        },
        "706a5cfb-e196-43d1-bbe3-c52aad9a46c1": {
            field: "comment",
            headerName: "COMMENT",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "e7210f4a-6e0a-4591-8428-e6845004ed04": {
            field: "crownheight",
            headerName: "CR.HEIGHT",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "c4e65984-1682-4c1e-812b-8938e9078b2d": {
            field: "crownangle",
            headerName: "CR.ANG",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "0f1de38d-3ae3-4209-a4d5-afe9f3018f22": {
            field: "culetsize",
            headerName: "CULET SZ",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "e3f77521-2c57-46bc-a02b-3c884685d2be": {
            field: "culetcondition",
            headerName: "CULET",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
            field: "depthpercent",
            headerName: "DEPTH%",
            headerAlign: "center",
            align: "center",
            editable: true,
            width: 75,
            renderCell: renderCellExpand,
        },
        "e83c2978-9088-49f0-9dc1-bcced73025fd": {
            field: "gridlemin",
            headerName: "GIRDLEMIN",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "cee57bac-9c19-40d5-b51a-51ed913dbf73": {
            field: "intensitylongtitle",
            headerName: "INTENSITYLONGTITLE",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "06d2a589-0d42-4dde-8724-acf2448c839a": {
            field: "laserinscription",
            headerName: "INS",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
            cellClassName: (params) => Utility.highlightLasertInscriptionCell(params.value)
        },
        "dd934505-1865-48cb-878f-f5d3c18af2d5": {
            field: "note",
            headerName: "NOTE",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "61f8a5f2-8516-419a-b6fd-39ef109cc2f4": {
            field: "paviliondepth",
            headerName: "PV.DEPTH",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
            field: "tablepercent",
            headerName: "TABLE%",
            headerAlign: "center",
            align: "center",
            editable: true,
            width: 75,
            renderCell: renderCellExpand,
        },
        "db078198-4416-4d53-b3d0-70d3a5418f65": {
            field: "open",
            headerName: "OPEN",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "e9028170-e978-4978-a2c9-1889c1821495": {
            field: "gridlecondition",
            headerName: "GIRDLE COND",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "fe8f55f3-358a-4418-bcdf-c3da4b01db67": {
            field: "gridlepercent",
            headerName: "GIRDLE%",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "daf69bba-bb1f-4d54-9dc7-9383ad429585": {
            field: "pavilionangle",
            headerName: "PV.ANGLE",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "ecfdfc0c-6a25-4a6d-9c8f-3406c23410af": {
            field: "white",
            headerName: "WHITE",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "7e356515-7cc5-4cd4-a1a4-a1b909b3b4e3": {
            field: "avgrapnetprice",
            headerName: "AVG RAP PRICE",
            headerAlign: "center",
            align: "center",
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.avgrapnetprice)
        },
        "a330b2b8-b9e4-40bb-bb7a-d0e83a717a74": {
            field: "avgdiscount",
            headerName: "AVG DIS",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.avgdiscount)
        },
        "1ac589a9-ff76-4b9a-9b6e-f0d7951dc0ca": {
            field: "avgprice",
            headerName: "AVG PRICE",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.avgprice)
        },
        "49b8f961-fe52-48ca-afbe-ceab5d7dc740": {
            field: "avgtotal",
            headerName: "AVG TOTAL",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.avgtotal)
        },
        "e32fe363-07cc-4307-82d2-82ae1069b5be": {
            field: "agenumber",
            headerName: "AGE",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "24ba0bf8-4ca1-4726-ad90-19a62c8c72e6": {
            field: "boxid",
            headerName: "BOXID",
            headerAlign: "center",
            align: "center",
            editable: true,
            renderCell: renderCellExpand,
        },
        "a1299782-d545-4d70-bcbc-7d576e396d64": {
            field: "length",
            headerName: "LENGTH",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            editable: true,
            renderCell: renderCellExpand,
        },
        "dfd5488a-b600-4e75-9735-98c26f78dc84": {
            field: "strln",
            headerName: "STAR LENGTH",
            headerAlign: "center",
            align: "center",
            width: 120,
            editable: true,
            renderCell: renderCellExpand,
        },
        "66dfbc62-477f-42f0-8097-56c5d8329156": {
            field: "lrhalf",
            headerName: "LR HALF",
            headerAlign: "center",
            align: "center",
            width: 120,
            editable: true,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "e683145c-29dd-4d41-af1a-518ff80090d2": {
            field: "keytosymbols",
            headerName: "KEY TO SYMBOLS",
            headerAlign: "center",
            align: "center",
            width: 220,
            editable: true,
            renderCell: renderCellExpand,
        },
    };

    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const processRowUpdate = (newRow, oldRow) => {
        const changedFeilds = Utility.getChangedFields(newRow, oldRow);
        if (changedFeilds.length > 0) {
            let { id, field, value } = changedFeilds[0];

            if (value === "")
                value = null;

            let updatedRows = [];
            rows.map(row => {
                let tRow = [];
                let colortype = row.colortype;
                if (!isNaN(parseFloat(value)) && row.stoneid === id) {
                    if (field === "discount") {
                        let price = parseFloat(Number(row.rapnetprice * ((100 + parseFloat(value)) / 100)).toFixed(2));
                        let totalPrice = price * row.size;
                        tRow = {
                            ...row,
                            [field]: parseFloat(value),
                            "price": price,
                            "total": totalPrice
                        };
                    }
                    else if (field === "price") {
                        let discount = parseFloat(((((row.rapnetprice - (Number(value))) / row.rapnetprice) * 100) * (-1)).toFixed(2));
                        let totalPrice = parseFloat((Number(value) * row.size).toFixed(2));
                        tRow = {
                            ...row,
                            [field]: parseFloat(value),
                            "discount": discount,
                            "total": totalPrice
                        };
                    }
                    else if (field === "total") {
                        let price = parseFloat((Number(value) / row.size).toFixed(2));
                        let discount = parseFloat(((((row.rapnetprice - (Number(price))) / row.rapnetprice) * 100) * (-1)).toFixed(2));
                        tRow = {
                            ...row,
                            [field]: parseFloat(value),
                            "discount": discount,
                            "price": price
                        };

                    }
                    else if (field === "rapnetprice") {
                        let price = parseFloat(Number(value * ((100 + Number(row.discount)) / 100)).toFixed(2));
                        let totalPrice = price * row.size;
                        tRow = {
                            ...row,
                            [field]: parseFloat(value),
                            "price": price,
                            "total": totalPrice
                        };
                    }
                    else if (colortype === "FANCY") {
                        if (field === "avgprice") {
                            let avgtotal = Number(
                                ((Number(value)) * row.size).toFixed(2));
                            tRow = {
                                ...row,
                                [field]: Number(value),
                                avgtotal: avgtotal,
                            };
                        }
                        else if (field === "avgtotal") {
                            let avgprice = Number(
                                ((Number(value)) / row.size).toFixed(2));
                            tRow = {
                                ...row,
                                [field]: Number(value),
                                avgprice: avgprice,
                            };
                        }
                        else {
                            tRow = row.stoneid === id ? { ...row, [field]: parseFloat(value) } : row;
                        }
                    }
                    else if (colortype === "WHITE") {
                        if (field === "avgdiscount") {
                            let ppc = Number(
                                (row.avgrapnetprice * ((1 + (Number(value)) / 100))).toFixed(2));
                            tRow = {
                                ...row,
                                [field]: Number(value),
                                avgprice: ppc,
                                avgtotal: Number((ppc * row.size).toFixed(2)),
                            };
                        }
                        else if (field === "avgprice") {
                            let avgdiscount = Number(((((row.avgrapnetprice - (Number(value))) / row.avgrapnetprice) * 100) * (-1)).toFixed(2));
                            tRow = {
                                ...row,
                                [field]: Number(value),
                                avgdiscount: avgdiscount,
                                avgtotal: Number((Number(value) * row.size).toFixed(2)),
                            };
                        }
                        else if (field === "avgrapnetprice") {
                            let ppc = (Number(value) + (Number(value) * (row.avgdiscount) / 100)).toFixed(2);
                            tRow = {
                                ...row,
                                [field]: Number(value),
                                avgprice: ppc,
                                avgtotal: Number((ppc * row.size).toFixed(2)),
                            };
                        }
                        else if (field === "avgtotal") {
                            let ppc = Number((Number(value) / row.size).toFixed(2));
                            tRow = {
                                ...row,
                                [field]: Number(value),
                                avgdiscount: Number(((((row.avgrapnetprice - (ppc)) / row.avgrapnetprice) * 100) * (-1)).toFixed(2)),
                                avgtotal: Number((ppc * row.size).toFixed(2)),
                                avgprice: Number(ppc),
                            };
                        } else if (field === "agenumber") {
                            const currentDate = new Date();
                            currentDate.setDate(currentDate.getDate() - value);
                            tRow = {
                                ...row,
                                agenumber: value,
                                age: Utility.ConvertLocalTimeToUtc(currentDate).slice(0, 10)
                            };
                        }
                        else {
                            tRow = row.stoneid === id ? { ...row, [field]: parseFloat(value) } : row;
                        }
                    }
                } else {
                    tRow = row.stoneid === id ? { ...row, [field]: value } : row;
                }
                updatedRows.push(tRow);
                return true;
            });
            setRows(updatedRows);
            setUpdatedData(updatedRows);

            // Return the updated row to exit edit mode
            return updatedRows.find(row => row.stoneid === id);
        }
        else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newRow };
        }
    };

    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanId}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}
            {showPacketDetailModal ? (
                <PacketHistoryModal
                    show={showPacketDetailModal}
                    packetId={packetId}
                    kapanId={kapanId}
                    close={() => setShowPacketDetailModal(false)}
                />
            ) : null}
            <Box
                sx={{
                    backgroundColor: '#fff',
                    color: '#000',
                    width: "100%",
                    position: "relative",
                    "& .MuiTablePagination-selectLabel": {
                        marginTop: "15px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                        marginTop: "15px",
                    },
                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                        color: "#1976d2",
                        padding: "4px 5px",
                        borderRadius: "4px",
                    },
                    "& .css-cc8tf1": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                        color: "#1976d2",
                        padding: "5px 5px",
                        borderRadius: "4px",
                    },
                    height: "50vh",
                }}
            >
                <Box sx={{ height: 400, width: '100%' }}>
                    <StyledDataGrid
                        disableRowSelectionOnClick
                        loading={detailLoader}
                        localeText={{ noRowsLabel: "No Record Found" }}
                        sx={{ fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight, }}
                        rows={rows}
                        columns={columnNames}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        getRowId={(row) => row.stoneid}
                        density="compact"
                        slots={{ toolbar: GridToolbar }}
                        initialState={{
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        apiRef={apiRef}
                        onCellClick={handleCellClick}
                        editMode="cell"
                    />
                </Box>
                <Box style={{ position: "absolute", top: "13px", right: "270px" }}>
                    {detailLoader ? (
                        <CircularProgress size="1rem" />
                    ) : (
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                onClick={() => handleSearch(stoneId)}
                                color="primary"
                                fontSize="medium"
                                sx={{ cursor: "pointer", mr: 1 }}
                            />
                        </Tooltip>
                    )}
                </Box>
            </Box>
        </div>
    );
};

export default StoneEdit;